import React from "react";
import { Link } from "react-router-dom";
import { ROUTE_NAMES } from "../routes/routeConstants";
import { AcaIcon } from "../components/shared/aca";
import { VELLUM as T, SANS, MONO, mono, body } from "../components/shared/folio/kit";
import { useAuth } from "../provider/AuthProvider";
import { useLoginModal } from "../context/LoginModalContext";

/* The close of the sheet.

   Drawn in the folio vocabulary rather than the old dark slab: the ground
   continues, a heavy rule signs it off, and the columns are separated by
   hairlines instead of by a change of material. Every link, the digest field
   and the legal row are exactly as they were - only the drawing changed. */

interface Col {
  title: string;
  items: { label: string; href: string }[];
}

/* The footer is a sitemap: the same for every visitor, signed in or not, and
   read when you are looking for where a thing lives rather than for your own
   account. Account-scoped destinations therefore do not belong here - they
   live behind the identity in the header. "Profile" sat in this first column
   and nowhere else, which filed a route open to both roles under one of them
   and left employers with no way to reach their own profile at all. */
const cols: Col[] = [
  {
    title: "For Job Seekers",
    items: [
      { label: "Browse Jobs", href: ROUTE_NAMES.PAGES.JOBS },
      { label: "Dashboard", href: ROUTE_NAMES.PAGES.JOB_SEEKER_DASHBOARD },
    ],
  },
  {
    title: "For Employers",
    items: [
      { label: "Dashboard", href: ROUTE_NAMES.PAGES.EMPLOYER_DASHBOARD },
      { label: "Post a Job", href: ROUTE_NAMES.PAGES.POST_JOB },
    ],
  },
  {
    title: "Company",
    items: [
      { label: "About Us", href: ROUTE_NAMES.PAGES.ABOUT },
      { label: "Blog", href: ROUTE_NAMES.PAGES.BLOG },
      { label: "Contact", href: ROUTE_NAMES.PAGES.CONTACT },
    ],
  },
];

export const Footer: React.FC = () => {
  const { user } = useAuth();
  const { openLoginModal } = useLoginModal();
  return (
    <footer
      style={{
        background: T.ground,
        color: T.ink,
        borderTop: `1.5px solid ${T.ink}`,
        fontFamily: SANS,
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "56px 20px 28px",
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          gap: 24,
        }}
        className="aca-footer-grid"
      >
        {/* Brand */}
        <div style={{ gridColumn: "span 4" }} className="aca-footer-brand">
          <Link to={ROUTE_NAMES.PAGES.HOME} style={{ display: "flex", alignItems: "center", gap: 10, color: "inherit", textDecoration: "none" }}>
            <div aria-hidden style={{ width: 14, height: 14, border: `1.5px solid ${T.accent}` }} />
            <div style={{ fontFamily: SANS, fontSize: 28, fontWeight: 700, letterSpacing: "-0.03em" }}>Acanetic</div>
          </Link>
          <div style={{ ...mono(T), marginTop: 12 }}>An index of academic appointments</div>
          <p style={{ ...body(T), fontSize: 14.5, lineHeight: 1.55, marginTop: 18, maxWidth: 340 }}>
            Connecting academic talent with world-class institutions. A quieter register of academic
            appointments, read and curated by working scholars.
          </p>
          <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
            {[
              { name: "twitter", href: "https://twitter.com/acanetic", icon: "rss" as const },
              { name: "linkedin", href: "https://linkedin.com/company/acanetic", icon: "globe" as const },
            ].map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="aca-footer-social"
                style={{
                  width: 32,
                  height: 32,
                  border: `1px solid ${T.rule}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: T.ink,
                  textDecoration: "none",
                }}
                aria-label={s.name}
              >
                <AcaIcon name={s.icon} size={14} />
              </a>
            ))}
          </div>
        </div>

        {cols.map((c) => (
          <div key={c.title} style={{ gridColumn: "span 2" }} className="aca-footer-col">
            <div style={{ ...mono(T), letterSpacing: "0.16em", marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${T.rule}` }}>
              {c.title}
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 9, fontSize: 14, fontFamily: SANS }}>
              {c.items.map((it) => (
                <li key={it.label}>
                  <Link
                    to={it.href}
                    className="aca-footer-link"
                    onClick={(e) => {
                      if (!user && it.href === ROUTE_NAMES.PAGES.JOBS) {
                        e.preventDefault();
                        openLoginModal();
                      }
                    }}
                    style={{ color: T.ink, textDecoration: "none" }}
                  >
                    {it.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Digest signup */}
        <div style={{ gridColumn: "span 2" }} className="aca-footer-digest">
          <div style={{ ...mono(T), letterSpacing: "0.16em", marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${T.rule}` }}>
            Digest
          </div>
          <input
            aria-label="Email address for the digest"
            placeholder="you@university.ac.uk"
            className="aca-footer-input"
            style={{
              width: "100%",
              background: "transparent",
              border: `1.5px solid ${T.ink}`,
              color: T.ink,
              padding: "10px 12px",
              fontFamily: SANS,
              fontSize: 13,
              outline: "none",
            }}
          />
          <button
            className="aca-footer-subscribe"
            style={{
              width: "100%",
              marginTop: 8,
              background: T.accent,
              color: T.onAccent,
              border: "none",
              padding: "10px 12px",
              fontFamily: MONO,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Subscribe ↗
          </button>
        </div>
      </div>

      <div
        style={{
          ...mono(T),
          letterSpacing: "0.16em",
          borderTop: `1px solid ${T.rule}`,
          padding: "18px 20px",
          maxWidth: 1280,
          margin: "28px auto 0",
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <span>© {new Date().getFullYear()} Acanetic. All rights reserved.</span>
        <div style={{ display: "flex", gap: 22 }}>
          <Link to={ROUTE_NAMES.PAGES.PRIVACY} className="aca-footer-link" style={{ color: "inherit", textDecoration: "none" }}>Privacy</Link>
          <Link to={ROUTE_NAMES.PAGES.TERMS} className="aca-footer-link" style={{ color: "inherit", textDecoration: "none" }}>Terms</Link>
          <Link to={ROUTE_NAMES.PAGES.GDPR} className="aca-footer-link" style={{ color: "inherit", textDecoration: "none" }}>GDPR</Link>
        </div>
      </div>

      <style>{`
        .aca-footer-link:hover { color: ${T.accent}; }
        .aca-footer-social:hover { border-color: ${T.ink}; color: ${T.accent}; }
        .aca-footer-subscribe:hover { background: ${T.accentDeep}; }
        .aca-footer-input:focus { border-color: ${T.accent}; }
        /* The theme's own placeholder measures 3.02:1 and fails WCAG AA;
           mute measures 4.96:1 against this ground. */
        .aca-footer-input::placeholder { color: ${T.mute}; }
        .aca-footer-link:focus-visible,
        .aca-footer-social:focus-visible,
        .aca-footer-subscribe:focus-visible,
        .aca-footer-input:focus-visible {
          outline: 2px solid ${T.accent};
          outline-offset: 3px;
        }

        @media (max-width: 900px) {
          .aca-footer-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .aca-footer-brand, .aca-footer-col, .aca-footer-digest { grid-column: span 1 !important; }
        }
      `}</style>
    </footer>
  );
};
